import { useCallback, type ComponentProps } from "react";
import { Stack, TextField } from "@mui/material";
import { debug as debugFn } from "debug";
import type { TOptions } from "i18next";
import { useTranslation } from "react-i18next";

import { tk } from "@thalamos/vision-common";

import { config } from "../../../config/index.js";
import type { useAdHocPlaceOfSafety } from "./useAdHocPlaceOfSafety.js";
import type { AdHocPlaceOfSafetyAddress } from "./useGoogleAutocomplete.js";
import { useGoogleAutocomplete } from "./useGoogleAutocomplete.js";

const debug = debugFn("vision-frontend:CollectAdHocPlaceOfSafety");

export type CollectAdHocPlaceOfSafetyProps = ReturnType<
  typeof useAdHocPlaceOfSafety
> & {
  translationOptions?: TOptions;
};

// This is extracted into it's own component because it is conditionally shown by the parent,
// and I found that if we always used the Google Autocomplete hook in the parent component,
// but only conditionally rendered the input, it would not predictably bind those things together.
// By grouping together the hook and input in this component, and controlling their visibility as one,
// we seem to avoid this issue.
export const CollectAdHocPlaceOfSafety = ({
  name,
  address,
  postalCode,
  setName,
  setAddress,
  setPostalCode,
  translationOptions,
}: CollectAdHocPlaceOfSafetyProps) => {
  const { t } = useTranslation();

  const handleGooglePlaceSelected = useCallback(
    (adHocPlaceOfSafety: AdHocPlaceOfSafetyAddress) => {
      debug("place selected", adHocPlaceOfSafety);
      setAddress(adHocPlaceOfSafety.address ?? "");
      setPostalCode(adHocPlaceOfSafety.postalCode ?? "");
    },
    [setAddress, setPostalCode],
  );

  return (
    <Stack spacing={2}>
      <TextField
        label={t(tk("placeOfSafetyPicker.name"), translationOptions)}
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
        data-testid="name-textfield"
      />
      <AddressTextField
        apiKey={config.googleMaps.apiKey}
        onPlaceSelected={handleGooglePlaceSelected}
        label={t(tk("placeOfSafetyPicker.address"), translationOptions)}
        value={address}
        onChange={(e) => {
          debug("manual address", e.target.value);
          setAddress(e.target.value);
        }}
        data-testid="address-textfield"
      />
      <TextField
        label={t(tk("placeOfSafetyPicker.postalCode"), translationOptions)}
        value={postalCode}
        onChange={(e) => {
          setPostalCode(e.target.value);
        }}
        data-testid="postalcode-textfield"
      />
    </Stack>
  );
};

type AddressTextFieldProps = ComponentProps<typeof TextField> & {
  apiKey: string;
  onPlaceSelected: (address: AdHocPlaceOfSafetyAddress) => void;
};

const SearchingAddressTextField = ({
  apiKey,
  onPlaceSelected,
  ...props
}: AddressTextFieldProps) => {
  const { ref } = useGoogleAutocomplete({ apiKey, onPlaceSelected });
  return <TextField {...props} inputRef={ref} />;
};

const AddressTextField = ({
  apiKey,
  onPlaceSelected,
  ...props
}: AddressTextFieldProps) =>
  apiKey === "" ? (
    <TextField {...props} />
  ) : (
    <SearchingAddressTextField
      apiKey={apiKey}
      onPlaceSelected={onPlaceSelected}
      {...props}
    />
  );
